import {
  ChatMessageWithParts,
  ContentType,
  ImageContent,
  SubAgentEvent,
  SubAgentRun,
} from "src/views/assistant/types";
import {
  EventSourceMessage,
  fetchEventSource,
} from "@microsoft/fetch-event-source";
import { authorizedUser, redirectToLogin } from "src/helpers/Auth";
import {
  filterPreferredPath,
  groupMessages,
} from "src/reusable/Chat/ChatStreaming/services/chatHelpers";
import {
  extractToolCallId,
  normalizeContentType,
} from "src/reusable/Chat/ChatStreaming/services/contentTypeUtils";

import { ChatConfiguration } from "../types";
import { ConversationRequest, ErrorMetadata } from "src/reusable/Chat/types";
import { DPO_FEEDBACK_TYPE } from "src/constants";
import { NucToast } from "src/reusable/components/Toast";
import { QueryClient } from "react-query";
import axios from "axios";
import { TAB_ID, TAB_ID_HEADER } from "src/helpers/tabId";
import { nuclearn_api_url } from "src/App";
import { StateCreator } from "zustand";
import { StreamingState } from "./StreamingStore";
import { createRequestPayload } from "src/reusable/Chat/utils/createRequestPayload";
import { newSendId, trackEvent } from "src/config/posthog";

export type TFetchFromSSE = {
  dialogue: ChatMessageWithParts[];
  activityId?: number | null;
  /** Images to include with the current message (for VLM support) */
  images?: ImageContent[];
};

/** Token usage data from model context window */
export interface TokenUsageData {
  current_tokens: number;
  max_tokens: number;
  percentage_used: number;
}

/** Queued message waiting for turn handoff */
export interface QueuedMessage {
  text: string;
  images?: ImageContent[];
  timestamp: number;
  /** Pairing key mirrored on the send-side ai_message_sent event */
  sendId?: string;
}

/** Per-send options threaded through for response-latency telemetry (#29704) */
export interface SendMessageOptions {
  /** Client-minted pairing key, mirrored on ai_message_sent / ai_response_completed */
  sendId?: string;
  /** How the message reached the send path — direct or via the turn-handoff queue */
  target?: "chat" | "queue" | "projects";
}

/** In-flight clock for the ai_response_completed event. Uses performance.now()
 *  (monotonic) — wall-clock diffs break when the system clock adjusts. */
export interface ResponseTiming {
  sentAt: number;
  firstTokenAt: number | null;
  sendId: string;
  target: "chat" | "queue" | "projects";
  /** True only when the send carried a caller-supplied sendId — i.e. a real
   *  composer send that also emitted ai_message_sent. Programmatic sends
   *  (artifact fallbacks, record resume) mint their own id and would otherwise
   *  emit an unpaired ai_response_completed that pollutes pairing analysis. */
  paired: boolean;
}

export interface CoreStreamingSlice {
  dialogue: ChatMessageWithParts[];
  responseSources: any[];
  /** Live Searched-tier entries streamed at tool completion (#20423). */
  searchedEntries: any[];
  isLoading: boolean;
  isConnected: boolean;
  isEnded: boolean;
  isMessageStreamStopped: boolean;
  /** True while the model is actively generating in the current turn.
   *  Set on stream connect / user_message, cleared on end event. */
  isTurnActive: boolean;
  connectionError: string | null;
  isConnectionError: boolean;
  streamError: string;
  errorMetadata: ErrorMetadata | null;
  messages: any[];
  metadata: any;
  /** Token usage data for context window gauge */
  tokenUsage: TokenUsageData | null;
  streamConfig: ChatConfiguration;
  abortController: AbortController | null;
  queryClient?: QueryClient;
  isNavigating: boolean;
  /** Current images to be sent with the next message (for VLM support) */
  pendingImages: ImageContent[];
  /** Last dialogue ID used by connectSSE — enables retry after transport errors */
  lastDialogueId: number | null;
  /** Monotonic count of flushed batches carrying model content (THINK/TEXT).
   *  Never reset — delivery watchers compare against a snapshot, and entry
   *  counts miss tokens merged into the last assistant message. */
  modelContentCount: number;
  /** When the user last hit Stop (terminateConnection) — lets watchers tell
   *  the user's own cancel from a turn that concluded on its own. */
  lastUserStopAt: number | null;
  /** Last tool_outcome_ack event: the server skipped a duplicate outcome
   *  because the prompt was already answered this session. */
  lastOutcomeAck: { prompt_id: string; already_answered: boolean } | null;
  /**
   * Sub-agents delegated to in this chat, in first-seen order and keyed
   * internally by subagent_dialogue_id. Each gets its own activity card.
   * Persists across turns as a record; cleared only by reset() (New chat).
   */
  activeSubAgents: {
    dialogueId: number;
    personaName: string;
    status: string;
    /** delegate_task tool-call id, for rendering the card inline. */
    parentToolCallId?: string;
    messages: SubAgentEvent[];
    /** Wall-clock start/end, held here so elapsed survives card remounts.
     *  Absent on hydrated (reloaded) cards — backend carries no timestamp. */
    startedAt?: number;
    endedAt?: number;
  }[];
  /** Messages queued locally, waiting to be sent when isTurnActive flips to false */
  pendingMessageQueue: QueuedMessage[];
  /** Latency clock for the current turn's ai_response_completed event;
   *  null when no send is in flight or the event already fired. */
  responseTiming: ResponseTiming | null;

  setStreamConfig: (newConfig: Partial<ChatConfiguration>) => void;
  emptySubmission: () => Promise<boolean>;
  /** Continue the conversation from current state without adding a user message */
  continueConversation: () => Promise<boolean>;
  /**
   * Optimistically flip the turn-active/loading flags after an out-of-band
   * send to a live session (e.g. a selection-prompt POST to /chat/send).
   * Those sends bypass sendUserMessage/fetchFromSSE, so nothing else lights
   * the typing indicator until the first SSE event of the new turn arrives —
   * during model latency the UI would otherwise look dead.
   */
  markTurnActive: () => void;
  /** Send a user message with optional images (for VLM support) */
  sendUserMessage: (
    text: string,
    images?: ImageContent[],
    opts?: SendMessageOptions,
  ) => Promise<boolean>;
  regenerateResponse: (id: string) => void;
  /** Resend a user message with updated text and/or images */
  resendUserMessage: (
    id: string,
    text: string,
    images?: ImageContent[],
  ) => void;
  setAsPreferredMessage: (messageId: string) => void;
  setDialogue: (
    dialogue:
      | ChatMessageWithParts[]
      | ((prev: ChatMessageWithParts[]) => ChatMessageWithParts[]),
  ) => void;
  setResponseSources: (sources: any[]) => void;
  setSearchedEntries: (entries: any[]) => void;
  clearChatErrors: () => void;
  terminateConnection: () => void;
  /**
   * Drop an open session that has no turn in flight (multi-turn sessions stay
   * open between turns). No stop signal, no error state: nothing was running.
   */
  closeIdleSession: () => void;
  setQueryClient: (queryClient: QueryClient) => void;
  fetchFromSSE: (params: TFetchFromSSE) => Promise<void>;
  resetCoreStream: () => void;
  /** Set images to be sent with the next message (for VLM support) */
  setPendingImages: (images: ImageContent[]) => void;
  /** Clear pending images after sending */
  clearPendingImages: () => void;
  /** Set token usage data (e.g., when loading dialogue from history) */
  setTokenUsage: (tokenUsage: TokenUsageData | null) => void;
  /** Connect to an existing agent stream (e.g., reconnect after page refresh).
   *  Skips POST to /chat/start — just opens SSE to /agent/stream/{id}.
   *  DB replay is skipped (dialogue already loaded); only live events are processed. */
  connectToStream: (
    dialogueId: number,
    opts?: { replay?: boolean; cursor?: string },
  ) => void;
  /** Retry the last SSE connection after a transport error.
   *  Re-opens the stream to lastDialogueId and clears error state. */
  retryConnection: () => void;
  /** Queue a message locally to be sent when the current turn ends */
  queueMessage: (
    text: string,
    images?: ImageContent[],
    opts?: Pick<SendMessageOptions, "sendId">,
  ) => void;
  /** Remove a queued message by index */
  removeQueuedMessage: (index: number) => void;
  /** Process the message queue — sends next message if turn is not active */
  processMessageQueue: () => void;
  /** Clear all queued messages (on disconnect/error) */
  clearMessageQueue: () => void;
  /** Restore sub-agent cards for a loaded dialogue from persisted runs.
   *  Merges by dialogue id so live cards are never clobbered. */
  hydrateSubAgents: (runs: SubAgentRun[]) => void;
}

/**
 * Whether the last assistant message has a tool call still executing. A Tool
 * row after it also answers its calls: the server sends the response for an
 * orphaned call as a message of its own. Responses close calls by
 * `tool_call_id`, so a call answered twice never closes another; a part
 * without an id closes one open call.
 */
function hasPendingToolCall(dialogue: ChatMessageWithParts[]): boolean {
  const last = dialogue.findLastIndex((m) => m.type === "Assistant");
  if (last === -1) return false;
  const parts = [
    dialogue[last],
    ...dialogue.slice(last + 1).filter((m) => m.type === "Tool"),
  ].flatMap((m) => m.contentParts || []);
  const open: string[] = [];
  const closed = new Set<string>();
  let unnamed = 0;
  for (const p of parts) {
    if (p.type === ContentType.TOOL_CALL) {
      const id = extractToolCallId(p.text);
      if (!id) unnamed++;
      else if (!open.includes(id) && !closed.has(id)) open.push(id);
    } else if (p.type === ContentType.TOOL_RESPONSE) {
      const id = extractToolCallId(p.text);
      if (id && open.includes(id)) {
        open.splice(open.indexOf(id), 1);
        closed.add(id);
      } else if (id && closed.has(id)) {
        // A second answer to a call already closed.
      } else if (unnamed > 0) unnamed--;
      else if (!id) open.shift();
    }
  }
  return open.length > 0 || unnamed > 0;
}

export const createCoreStreamingSlice =
  (
    initialConfig: ChatConfiguration = {},
  ): StateCreator<
    StreamingState,
    [["zustand/subscribeWithSelector", never]],
    [],
    CoreStreamingSlice
  > =>
  (set, get) => {
    const initialDialogue: ChatMessageWithParts[] =
      initialConfig.initialUserMessage
        ? [
            {
              type: "User",
              contentParts: [
                {
                  type: ContentType.TEXT,
                  text: initialConfig.initialUserMessage,
                },
              ],
              feedback_value: null,
            },
          ]
        : [];

    // Fire ai_response_completed (#29704) exactly once per turn. Clears the
    // clock before capturing so racing terminal handlers (error then end,
    // end then status) can't double-emit. No-op when no send is in flight
    // (regenerate/resend/replay turns, reconnects) or on non-AtomAssist
    // surfaces sharing this store.
    const emitResponseCompleted = (
      status: "completed" | "error" | "stopped",
    ) => {
      const { responseTiming, streamConfig, lastDialogueId } = get();
      if (!responseTiming) return;
      set({ responseTiming: null });
      if (streamConfig?.product !== "atom-assist") return;
      // Only paired composer sends have a matching ai_message_sent; skip
      // programmatic sends so they don't emit an orphan completion.
      if (!responseTiming.paired) return;
      const now = performance.now();
      trackEvent("ai_response_completed", {
        // lastDialogueId fallback: on a brand-new chat the config only picks
        // up the server-assigned id after navigation, but connectSSE has
        // already recorded it.
        dialogue_id: streamConfig?.dialogueId ?? lastDialogueId ?? null,
        send_id: responseTiming.sendId,
        target: responseTiming.target,
        status,
        total_ms: Math.round(now - responseTiming.sentAt),
        time_to_first_token_ms:
          responseTiming.firstTokenAt !== null
            ? Math.round(responseTiming.firstTokenAt - responseTiming.sentAt)
            : null,
      });
    };

    // Helper: connect to agent stream SSE via @microsoft/fetch-event-source.
    // Shared by fetchFromSSE (after POST /chat/start) and connectToStream (reconnect).
    // Options:
    //   skipReplay (default true) — pass skip_replay=true to skip DB replay
    //     (history already loaded). Involuntary drops auto-reconnect to the same
    //     URL; the library resends Last-Event-ID and the backend resumes the
    //     durable stream from that cursor. Set false for retryConnection's full
    //     DB replay fallback.
    const connectSSE = (
      dialogueId: number,
      opts: { skipReplay?: boolean; lastEventId?: string } = {},
    ) => {
      const { skipReplay = true, lastEventId } = opts;
      const { streamConfig: config, queryClient, resetParsingState } = get();
      const user = authorizedUser();
      const { onMetadataEvent = () => {}, onMessageEvent = () => {} } = config;

      // Abort any existing connection
      const { abortController: existingCtrl } = get();
      if (existingCtrl) existingCtrl.abort();

      const ctrl = new AbortController();
      set({ abortController: ctrl, lastDialogueId: dialogueId });

      // Auth: backend requires JWT as query param (FastAPI Query(...) parameter).
      // skipReplay=true: dialogue already loaded via useLoadDialogue, skip DB replay.
      // skipReplay=false: retryConnection — replay from DB to recover missed events.
      const token = (user.Authorization || "").replace(/^bearer\s+/i, "");
      const replayParam = skipReplay ? "&skip_replay=true" : "";
      const streamUrl = `${nuclearn_api_url}/agent/stream/${dialogueId}?token=${encodeURIComponent(token)}${replayParam}`;

      // Auto-retry: track transport error count so the library can retry
      // a few times before giving up and showing the error to the user.
      const MAX_AUTO_RETRIES = 3;
      let transportRetryCount = 0;

      // RAF batching: collect high-frequency message events and flush once per frame.
      // Control events (error, end, status) are applied immediately.
      let pendingMessages: any[] = [];
      let rafId: number | null = null;
      let receivedModelContent = false;

      const flushMessages = () => {
        rafId = null;
        if (pendingMessages.length === 0) return;
        const batch = pendingMessages;
        pendingMessages = [];

        const batchHasModelContent = batch.some((msg: any) => {
          const ct = normalizeContentType(msg.content_type);
          return ct === ContentType.THINK || ct === ContentType.TEXT;
        });

        // Keep isLoading true until we receive the first thinking or text
        // token from the model, so the spinner persists through the gap
        // between SSE connection and actual model output.
        if (!receivedModelContent) {
          receivedModelContent = batchHasModelContent;
        }

        // First model token since the send — stamp TTFT. Deliberately not
        // gated on receivedModelContent: that flag only resets when a
        // user_message event echoes back, which doesn't happen for sends
        // injected into a live session, and a fresh responseTiming
        // (firstTokenAt === null) is the real turn boundary here.
        if (batchHasModelContent) {
          const timing = get().responseTiming;
          if (timing && timing.firstTokenAt === null) {
            set({
              responseTiming: { ...timing, firstTokenAt: performance.now() },
            });
          }
        }

        // Single Zustand set() for all queued messages
        const lastTokenUsage = batch.reduce(
          (acc, d) => d.token_usage || acc,
          null as any,
        );
        set((state) => ({
          isLoading: receivedModelContent ? false : state.isLoading,
          isEnded: false,
          messages: [...state.messages, ...batch],
          ...(batchHasModelContent
            ? { modelContentCount: state.modelContentCount + 1 }
            : {}),
          ...(lastTokenUsage ? { tokenUsage: lastTokenUsage } : {}),
        }));

        // Fire callbacks after state update — get() inside loop so each
        // callback sees artifacts/dialogue from prior callbacks in the batch
        batch.forEach((data) => onMessageEvent(data, get()));
      };

      const scheduleFlush = () => {
        if (rafId === null) {
          rafId = requestAnimationFrame(flushMessages);
        }
      };

      fetchEventSource(streamUrl, {
        method: "GET",
        // Lowercase key: the library overwrites "last-event-id" with newer
        // received ids on auto-retry, so the freshest cursor always wins.
        headers: {
          Authorization: user.Authorization || "",
          ...(lastEventId ? { "last-event-id": lastEventId } : {}),
        },
        signal: ctrl.signal,
        openWhenHidden: true,

        async onopen(response) {
          if (response.ok) {
            transportRetryCount = 0; // Reset on successful connection
            set({
              isConnected: true,
              connectionError: null,
              isConnectionError: false,
            });
          } else if (response.status === 401 || response.status === 403) {
            set({ abortController: null, isConnected: false });
            redirectToLogin();
            throw new Error("Unauthorized");
          } else {
            throw new Error(`Server error: ${response.status}`);
          }
        },

        onmessage(event: EventSourceMessage) {
          const eventType = event.event || "message";

          // Keepalive ping from sse_starlette — ignore but reset retry count
          if (eventType === "ping") {
            transportRetryCount = 0;
            return;
          }

          if (eventType === "replay_complete") {
            // Replay phase complete — flush any pending messages before live events.
            flushMessages();
            return;
          }

          if (eventType === "subagent") {
            try {
              const data: SubAgentEvent = JSON.parse(event.data || "{}");
              const id = data.subagent_dialogue_id;
              set((state) => {
                const existing = state.activeSubAgents;
                const idx = existing.findIndex((sa) => sa.dialogueId === id);
                if (idx >= 0) {
                  // Append to the matching sub-agent. A terminal status
                  // (completed/error) just updates this card's status — we
                  // keep it visible so the user retains a record of what each
                  // delegated agent did.
                  const nextStatus = data.status || existing[idx].status;
                  const terminal =
                    nextStatus === "completed" || nextStatus === "error";
                  const updated = {
                    ...existing[idx],
                    status: nextStatus,
                    parentToolCallId:
                      existing[idx].parentToolCallId ??
                      data.parent_tool_call_id,
                    messages: [...existing[idx].messages, data],
                    // Freeze elapsed at the first terminal event.
                    endedAt:
                      existing[idx].endedAt ??
                      (terminal ? Date.now() : undefined),
                  };
                  const next = existing.slice();
                  next[idx] = updated;
                  return { activeSubAgents: next };
                }
                // First event for a newly-spawned sub-agent — add a card.
                const status = data.status || "started";
                return {
                  activeSubAgents: [
                    ...existing,
                    {
                      dialogueId: id,
                      personaName: data.persona_name,
                      status,
                      parentToolCallId: data.parent_tool_call_id,
                      messages: [data],
                      startedAt: Date.now(),
                      endedAt:
                        status === "completed" || status === "error"
                          ? Date.now()
                          : undefined,
                    },
                  ],
                };
              });
            } catch (e) {
              console.error("[connectSSE] Error parsing subagent event:", e);
            }
            return;
          }

          if (eventType === "user_message") {
            // Backend echoes queued user messages when they are picked up
            // at a cycle boundary or between turns. Insert into dialogue
            // so the message appears in the chat UI at the correct position.
            //
            // Skip if this message is already visible in the dialogue. Queued
            // follow-ups may have been inserted optimistically in the UI before
            // the backend's explicit user_message event arrives.
            flushMessages(); // Flush pending assistant tokens first
            try {
              const data = JSON.parse(event.data || "{}");
              const content = data.content || "";
              const { dialogue: currentDialogue } = get();
              const lastVisibleUser = [...currentDialogue]
                .reverse()
                .find((message) => message.type === "User");
              if (
                lastVisibleUser &&
                lastVisibleUser.contentParts?.[0]?.text === content
              ) {
                // Already visible — just mark the turn active.
                receivedModelContent = false;
                set({ isLoading: true, isTurnActive: true });
                return;
              }
              const images: ImageContent[] | undefined = data.images?.length
                ? data.images
                : undefined;
              receivedModelContent = false;
              set((state) => ({
                dialogue: [
                  ...state.dialogue,
                  {
                    type: "User" as const,
                    contentParts: [{ type: ContentType.TEXT, text: content }],
                    images,
                    feedback_value: null,
                  },
                ],
                isLoading: true,
                isTurnActive: true,
              }));
            } catch (e) {
              console.error("[connectSSE] Error parsing user_message:", e);
            }
            return;
          }

          if (eventType === "message") {
            // Process all messages (replay and live) through the normal pipeline.
            // On retry (skipReplay=false), dialogue was cleared in retryConnection
            // so replay messages cleanly rebuild it without duplicates.
            // On normal connect (skipReplay=true), backend skips replay entirely.
            try {
              const data = JSON.parse(event.data || "{}");
              pendingMessages.push(data);
              scheduleFlush();
            } catch (e) {
              console.error("[connectSSE] Error parsing message:", e);
            }
            return;
          }

          if (eventType === "tool_outcome_ack") {
            // Own branch, not "metadata": that branch force-sets isEnded false.
            flushMessages();
            try {
              const data = JSON.parse(event.data || "{}");
              // No turn follows a skip — clear the optimistic markTurnActive
              // state like "end" does, or the spinner runs to the idle timeout.
              set({
                lastOutcomeAck: {
                  prompt_id: String(data.prompt_id ?? ""),
                  already_answered: Boolean(data.already_answered),
                },
                isLoading: false,
                isTurnActive: false,
              });
              setTimeout(() => get().processMessageQueue(), 50);
            } catch (e) {
              console.error("[connectSSE] Error parsing tool_outcome_ack:", e);
            }
            return;
          }

          if (eventType === "metadata") {
            // Flush any pending messages first to preserve ordering
            flushMessages();
            try {
              const data = JSON.parse(event.data || "{}");
              set((state) => ({
                isEnded: false,
                metadata: { ...state.metadata, metadataEvent: data },
                ...(data.token_usage ? { tokenUsage: data.token_usage } : {}),
              }));
              onMetadataEvent(data, get());
            } catch (e) {
              console.error("[connectSSE] Error parsing metadata:", e);
            }
            return;
          }

          if (eventType === "error") {
            flushMessages(); // Flush before terminal event
            if (!event.data) return;
            let normalized = "";
            let tokenExpired = false;
            let errorMeta: ErrorMetadata | null = null;

            try {
              const parsed = JSON.parse(event.data);
              if (parsed && typeof parsed === "object") {
                if (parsed.message) {
                  normalized = String(parsed.message);
                  errorMeta = {
                    error_code: parsed.error_code,
                    requires_new_chat: parsed.requires_new_chat,
                    model_id: parsed.model_id,
                  };
                } else {
                  normalized = JSON.stringify(parsed);
                }
                if (parsed.token_expired) tokenExpired = true;
              } else {
                normalized = String(event.data);
              }
            } catch {
              normalized = String(event.data);
            }

            if (tokenExpired) normalized = normalized || "Session expired";

            emitResponseCompleted("error");
            set({
              isEnded: true,
              isLoading: false,
              isMessageStreamStopped: true,
              isTurnActive: false,
              streamError: normalized,
              errorMetadata: errorMeta,
              abortController: null,
              isConnected: false,
              pendingMessageQueue: [], // Clear queue on error
            });
            resetParsingState();
            ctrl.abort();
            if (tokenExpired) redirectToLogin();
            return;
          }

          if (eventType === "end") {
            flushMessages(); // Flush before cycle boundary
            // Emit even if a tool response is still in flight (#1525 early
            // "end") — a rare slight undercount beats a missing sample,
            // which would bias latency percentiles.
            emitResponseCompleted("completed");
            // "end" = the assistant turn finished. The agent service emits
            // this exactly once per turn, after all tool-call cycles are
            // done. The SSE session may stay open for follow-up turns
            // (multi_turn mode), so isConnected/abortController are left
            // intact — but the input bar must swing back to "ready" or
            // the user is forced to click stop to type again.
            try {
              const data = JSON.parse(event.data || "{}");
              if (data.token_usage) set({ tokenUsage: data.token_usage });
            } catch {
              // Ignore parse errors for end event
            }

            // Keep "active" state if there's an unanswered tool call still
            // in flight (#1525) — a flaky generator can drop "end" before
            // the tool_response chunk arrives, and we don't want the
            // input to come back mid-tool.
            const activeTool = hasPendingToolCall(get().dialogue);
            if (activeTool) {
              set({ isLoading: false });
            } else {
              set({
                isLoading: false,
                isTurnActive: false,
              });
              // Process queued messages if turn ended (no pending tool call)
              setTimeout(() => get().processMessageQueue(), 50);
            }
            return;
          }

          if (eventType === "status") {
            flushMessages(); // Flush before terminal event
            try {
              const data = JSON.parse(event.data || "{}");
              const status = typeof data === "string" ? data : data?.status;
              if (status === "completed" || status === "error") {
                // Backstop for turns whose "end" event never arrived — the
                // clock was already cleared on the normal path.
                emitResponseCompleted(
                  status === "error" ? "error" : "completed",
                );
                if (status === "error") {
                  set({ streamError: data?.error || "Session error" });
                }
                set({
                  isLoading: false,
                  isEnded: true,
                  isMessageStreamStopped: true,
                  isTurnActive: false,
                  abortController: null,
                  isConnected: false,
                  // Keep activeSubAgents visible as a record; only reset()
                  // (New chat) clears them.
                  pendingMessageQueue: [], // Clear queue on terminal events
                });
                resetParsingState();
                if (queryClient) {
                  queryClient.invalidateQueries(["dialogues"]);
                }
                ctrl.abort();
              }
            } catch {
              // Ignore parse errors for status event
            }
            return;
          }
        },

        onclose() {
          flushMessages();
          if (rafId !== null) cancelAnimationFrame(rafId);
          const { isEnded, isTurnActive, isLoading } = get();

          // A terminal status event already ran: normal completion.
          if (isEnded) {
            ctrl.abort();
            set({ abortController: null, isConnected: false });
            if (queryClient) {
              queryClient.invalidateQueries(["dialogues"]);
            }
            throw new Error("Stream closed");
          }

          // Idle between turns (the session stays open after `end` in
          // multi-turn mode) and the server dropped it: nothing was running,
          // so record the close without an error and without a retry.
          if (!isTurnActive && !isLoading) {
            ctrl.abort();
            set({ abortController: null, isConnected: false });
            throw new Error("Session closed while idle");
          }

          // Dropped mid-turn with no terminal event. fetch-event-source
          // reconnects only when onclose throws (a plain return disposes the
          // source), so hand the drop to onerror: it counts the attempt,
          // backs off, and the library resumes from Last-Event-ID.
          throw new Error("Stream dropped mid-turn");
        },

        onerror(err) {
          if (rafId !== null) cancelAnimationFrame(rafId);
          flushMessages();
          if (ctrl.signal.aborted) {
            // Intentional close (terminateConnection, status event, error event)
            throw err; // Stop retry, don't set error state
          }

          // Transport error — let the library auto-retry with backoff
          transportRetryCount++;
          if (transportRetryCount <= MAX_AUTO_RETRIES) {
            console.warn(
              `[connectSSE] Transport error, auto-retry ${transportRetryCount}/${MAX_AUTO_RETRIES}`,
            );
            set({ isConnected: false });
            // Return delay in ms — library will retry after this interval
            return 1000 * Math.pow(2, transportRetryCount - 1); // 1s, 2s, 4s
          }

          // Max retries exhausted — show error to user
          emitResponseCompleted("error");
          set({
            isConnected: false,
            isLoading: false,
            isEnded: true,
            isMessageStreamStopped: true,
            isTurnActive: false,
            connectionError: "Error connecting to the server",
            isConnectionError: true,
            abortController: null,
          });
          resetParsingState();
          throw err;
        },
      }).catch(() => {
        // fetchEventSource rejects when we throw from onclose/onerror after
        // max retries. State is already set in the handlers — nothing to do.
      });
    };

    return {
      dialogue: initialDialogue,
      responseSources: [],
      searchedEntries: [],
      isLoading: false,
      isConnected: false,
      isEnded: true,
      isMessageStreamStopped: false,
      isTurnActive: false,
      connectionError: null,
      isConnectionError: false,
      streamError: "",
      errorMetadata: null,
      messages: [],
      metadata: {},
      tokenUsage: null,
      streamConfig: {
        api: initialConfig.api || "",
        onMetadataEvent: initialConfig.onMetadataEvent || (() => {}),
        onMessageEvent: initialConfig.onMessageEvent || (() => {}),
        datasetsForSearchContext: initialConfig.datasetsForSearchContext || [],
        textGenModelParams: initialConfig.textGenModelParams || {},
        ...initialConfig,
      },
      abortController: null,
      queryClient: undefined,
      isNavigating: false,
      pendingImages: [],
      lastDialogueId: null,
      modelContentCount: 0,
      lastUserStopAt: null,
      lastOutcomeAck: null,
      activeSubAgents: [],
      pendingMessageQueue: [],
      responseTiming: null,

      setPendingImages: (images: ImageContent[]) => {
        set({ pendingImages: images });
      },
      clearPendingImages: () => {
        set({ pendingImages: [] });
      },
      setTokenUsage: (tokenUsage: TokenUsageData | null) => {
        set({ tokenUsage });
      },
      setStreamConfig: (newConfig: Partial<ChatConfiguration>) => {
        set((state) => ({
          streamConfig: { ...state.streamConfig, ...newConfig },
        }));
      },
      setDialogue: (dialogue) => {
        set((state) => ({
          dialogue:
            typeof dialogue === "function"
              ? dialogue(state.dialogue)
              : dialogue,
        }));
      },
      setResponseSources: (sources) => {
        // Merge-with-dedup by data_chunk.id. Workspace tools (read/grep)
        // emit one metadata event per call; a pure replace would let the
        // last call clobber every earlier source from the same turn (e.g.
        // a PDF read followed by an XER read would lose the PDF source).
        // Per-turn reset happens in fetchFromSSE; resetCoreStream handles
        // full chat clears.
        set((state) => {
          const seen = new Set<number | string>();
          const merged: any[] = [];
          for (const src of [...state.responseSources, ...sources]) {
            const id = src?.data_chunk?.id;
            if (id == null) {
              merged.push(src);
              continue;
            }
            if (!seen.has(id)) {
              seen.add(id);
              merged.push(src);
            }
          }
          return { responseSources: merged };
        });
      },
      setSearchedEntries: (entries) => {
        // Append-with-dedup: one metadata event per retrieval call, so
        // duplicates only appear on SSE redelivery. The stamped timestamp
        // distinguishes a redelivered entry from a legit re-run of the same
        // query. Per-turn reset happens in fetchFromSSE, like responseSources.
        set((state) => {
          const key = (e: any) =>
            `${e?.timestamp ?? ""}|${e?.query_text ?? ""}|${e?.tool_name ?? ""}`;
          const seen = new Set(state.searchedEntries.map(key));
          const fresh = (entries ?? []).filter((e: any) => {
            const k = key(e);
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
          });
          if (!fresh.length) return state;
          return { searchedEntries: [...state.searchedEntries, ...fresh] };
        });
      },
      clearChatErrors: () => {
        set({
          connectionError: null,
          isConnectionError: false,
          streamError: "",
        });
      },

      closeIdleSession: () => {
        const { abortController } = get();
        if (abortController) abortController.abort();
        set({ abortController: null, isConnected: false });
      },

      terminateConnection: () => {
        const {
          abortController,
          isNavigating,
          streamConfig,
          resetParsingState,
        } = get();

        // Always send backend stop signal, even if we have no local SSE
        // connection (e.g., page was refreshed while agent was running).
        const dialogueId =
          streamConfig?.dialogueId ||
          (() => {
            const match = window.location.pathname.match(
              /\/(?:chat|agent)\/(\d+)/,
            );
            return match ? parseInt(match[1]) : undefined;
          })();

        if (dialogueId) {
          axios
            .post(`${nuclearn_api_url}/chat/send/${dialogueId}`, {
              type: "stop",
            })
            .catch((err) =>
              console.warn("[terminateConnection] Stop request failed:", err),
            );
        } else {
          console.warn(
            "[terminateConnection] No dialogueId — cannot send stop signal",
          );
        }

        // Abort the fetchEventSource connection
        if (abortController && !isNavigating) {
          abortController.abort();
        }
        emitResponseCompleted("stopped");
        set({
          abortController: null,
          isConnected: false,
          isLoading: false,
          isEnded: true,
          isMessageStreamStopped: true,
          isTurnActive: false,
          lastUserStopAt: Date.now(),
          pendingMessageQueue: [], // Clear queue on stop
        });
        resetParsingState();
      },
      setQueryClient: (queryClient: QueryClient) => {
        set({ queryClient });
      },

      fetchFromSSE: async ({
        dialogue,
        activityId = null,
        images,
      }: TFetchFromSSE) => {
        const { streamConfig: config, resetParsingState } = get();
        if (!config) {
          console.error("StreamingBase config not set");
          return;
        }

        let {
          modelId,
          modelVersionNum,
          datasetsForSearchContext = [],
          sourceDataRecordIds,
          numSearchResults,
          dialogueId,
          textGenModelParams,
          systemPrompt,
          api = "",
          isOP,
          chatConfigId,
          product,
          images: configImages,
          productContext,
          getViewContext,
          responseMode,
        } = config;

        // Use images from params if provided, otherwise from config
        const messageImages = images || configImages;

        textGenModelParams = {
          ...textGenModelParams,
        };

        const user = authorizedUser();
        if (user.invalid) {
          redirectToLogin();
          return;
        }

        // Abort any existing connection before starting a new one
        const { abortController: existingCtrl } = get();
        if (existingCtrl) {
          existingCtrl.abort();
        }

        // Create abort controller early so terminateConnection can cancel the
        // POST to /chat/start (not just the SSE). connectSSE will replace it.
        const fetchCtrl = new AbortController();
        set({
          abortController: fetchCtrl,
          connectionError: null,
          isConnectionError: false,
          streamError: "",
          isLoading: true,
          isEnded: false,
          isMessageStreamStopped: false,
          isTurnActive: true,
          // New user turn — clear sources so the panel scopes to this answer.
          // setResponseSources merges with existing state, so without this
          // reset, sources would accumulate across the whole conversation.
          responseSources: [],
          searchedEntries: [],
        });

        // Get dialogueId from URL query params
        const getQueryParam = new URLSearchParams(window.location.search).get(
          "dialogueId",
        );
        const currentDialogueId =
          dialogueId || (getQueryParam ? parseInt(getQueryParam) : undefined);

        let postData: ConversationRequest;
        try {
          postData = createRequestPayload({
            api,
            modelId,
            modelVersionNum,
            dialogue: dialogue,
            datasetsForSearchContext,
            sourceDataRecordIds,
            numSearchResults,
            dialogueId: currentDialogueId,
            textGenModelParams,
            systemPrompt,
            isOP,
            activityId,
            chatConfigId,
            images: messageImages,
            product,
            productContext,
            // Snapshot the chart-panel view at /chat/start time too —
            // first turn's prompt provider sees it without waiting for a
            // /chat/send round-trip to refresh the persisted state.
            viewContext: getViewContext ? getViewContext() : undefined,
            responseMode,
          });
        } catch (error) {
          console.error("Error creating request payload:", error);
          set({
            streamError: (error as Error).message,
            isLoading: false,
            isEnded: true,
            isTurnActive: false,
            isConnectionError: true,
          });
          resetParsingState();
          return;
        }

        // Detect endpoint type: /chat/start uses two-step (POST → SSE),
        // legacy endpoints (e.g., OP's /observation-program/converse) use
        // direct POST-SSE where the response body is the event stream.
        const isAgentStreamEndpoint = api.endsWith("/chat/start");

        if (isAgentStreamEndpoint) {
          // Two-step flow: POST /chat/start → GET /agent/stream/{id}
          let responseDialogueId: number;
          try {
            const response = await axios.post(
              api,
              { conversation: postData, mode: "multi_turn" },
              {
                headers: {
                  Authorization: user.Authorization,
                  "Content-Type": "application/json",
                },
                signal: fetchCtrl.signal, // Cancelled by terminateConnection
              },
            );
            responseDialogueId = response.data.dialogue_id;
          } catch (error: any) {
            // User clicked Stop during POST — don't show error
            if (fetchCtrl.signal.aborted) return;
            const errorMessage =
              error.response?.data?.detail ||
              error.message ||
              "Failed to start chat session";
            console.error("[fetchFromSSE] /chat/start failed:", errorMessage);
            emitResponseCompleted("error");
            set({
              streamError: errorMessage,
              isLoading: false,
              isEnded: true,
              isTurnActive: false,
              isConnectionError: true,
            });
            resetParsingState();
            return;
          }

          // Guard: if the user clicked Stop while POST was in flight, don't
          // open SSE — the turn was already cancelled.
          if (fetchCtrl.signal.aborted) return;

          // Stash the id on streamConfig now rather than waiting for the
          // turn-1 SSE metadata event: consumers gate on it (ad-hoc upload
          // target, share/URL sync, /chat/send routing) and the metadata
          // event arrives seconds later — or never, if the subscriber missed
          // it in Redis pub/sub.
          set((state) => ({
            streamConfig: {
              ...state.streamConfig,
              dialogueId: responseDialogueId,
            },
          }));

          connectSSE(responseDialogueId, { skipReplay: true });
        } else {
          // Legacy direct-POST-SSE: POST payload to the API endpoint,
          // response body IS the SSE stream (used by OP and any other
          // product that hasn't migrated to /chat/start).
          const {
            onMetadataEvent: onMeta = () => {},
            onMessageEvent: onMsg = () => {},
          } = config;

          const legacyCtrl = new AbortController();
          set({ abortController: legacyCtrl });

          fetchEventSource(api, {
            method: "POST",
            openWhenHidden: true,
            signal: legacyCtrl.signal,
            headers: {
              ...(user.Authorization
                ? { Authorization: user.Authorization }
                : {}),
              "Content-Type": "application/json",
              [TAB_ID_HEADER]: TAB_ID,
            },
            body: JSON.stringify(postData),
            async onopen(response) {
              if (response.ok) {
                set({
                  isConnected: true,
                  connectionError: null,
                  isConnectionError: false,
                });
              } else {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                let errorMeta: ErrorMetadata | null = null;
                try {
                  const errorData = await response.json();
                  const detail = errorData.detail;
                  if (typeof detail === "string") {
                    errorMessage = detail;
                  } else if (detail?.message) {
                    errorMessage = detail.message;
                    errorMeta = detail;
                  }
                } catch {
                  // Use default error message
                }
                set({
                  streamError: errorMessage,
                  errorMetadata: errorMeta,
                  isLoading: false,
                  isEnded: true,
                  isConnectionError: true,
                });
                throw new Error(errorMessage);
              }
            },
            onmessage(event: EventSourceMessage) {
              set({ streamError: "", connectionError: null });
              try {
                if (event.event === "metadata") {
                  const metadataEvent = JSON.parse(event.data);
                  set((state) => ({
                    isEnded: false,
                    metadata: { ...state.metadata, metadataEvent },
                    ...(metadataEvent.token_usage
                      ? { tokenUsage: metadataEvent.token_usage }
                      : {}),
                  }));
                  onMeta(metadataEvent, get());
                } else if (event.event === "message") {
                  const data = JSON.parse(event.data);
                  set((state) => ({
                    isLoading: false,
                    isEnded: false,
                    messages: [...state.messages, data],
                    ...(data.token_usage
                      ? { tokenUsage: data.token_usage }
                      : {}),
                  }));
                  onMsg(data, get());
                } else if (event.event === "end") {
                  // One inference cycle complete — extract token usage.
                  try {
                    const data = JSON.parse(event.data || "{}");
                    if (data.token_usage) set({ tokenUsage: data.token_usage });
                  } catch {
                    // Ignore parse errors for end event
                  }
                  // Keep isTurnActive if a tool call is pending (#1525)
                  const legacyActiveTool = hasPendingToolCall(get().dialogue);
                  set({
                    isLoading: false,
                    ...(!legacyActiveTool && { isTurnActive: false }),
                  });
                } else if (event.event === "error") {
                  if (!event.data) return;
                  let normalized = "";
                  let tokenExpired = false;
                  let errorMeta: ErrorMetadata | null = null;

                  try {
                    const parsed = JSON.parse(event.data);
                    if (parsed && typeof parsed === "object") {
                      if (parsed.message) {
                        normalized = String(parsed.message);
                        errorMeta = {
                          error_code: parsed.error_code,
                          requires_new_chat: parsed.requires_new_chat,
                          model_id: parsed.model_id,
                        };
                      } else {
                        normalized = JSON.stringify(parsed);
                      }
                      if (parsed.token_expired) tokenExpired = true;
                    } else {
                      normalized = String(event.data);
                    }
                  } catch {
                    normalized = String(event.data);
                  }

                  if (tokenExpired)
                    normalized = normalized || "Session expired";

                  set({
                    streamError: normalized,
                    errorMetadata: errorMeta,
                    isEnded: true,
                    isLoading: false,
                    isMessageStreamStopped: true,
                    abortController: null,
                    isConnected: false,
                  });
                  resetParsingState();
                  legacyCtrl.abort();
                  if (tokenExpired) redirectToLogin();
                }
              } catch (e) {
                console.error("[fetchFromSSE] Legacy event error:", e);
              }
            },
            onclose() {
              // Mark as intentional so onerror (where fetchEventSource
              // routes onclose exceptions) doesn't set isConnectionError.
              legacyCtrl.abort();
              set({
                abortController: null,
                isConnected: false,
                isLoading: false,
                isEnded: true,
                isMessageStreamStopped: true,
                isTurnActive: false,
              });
              resetParsingState();
              const qc = get().queryClient;
              if (qc) {
                qc.invalidateQueries(["dialogues"]);
              }
              throw new Error("Stream closed");
            },
            onerror(err) {
              if (legacyCtrl.signal.aborted) throw err;
              set({
                isConnected: false,
                isLoading: false,
                isEnded: true,
                isMessageStreamStopped: true,
                connectionError: "Error connecting to the server",
                isConnectionError: true,
                abortController: null,
              });
              resetParsingState();
              throw err;
            },
          }).catch(() => {
            // State already set in handlers
          });
        }
      },
      regenerateResponse: (id: string) => {
        const {
          isConnected,
          dialogue,
          fetchFromSSE,
          clearArtifactsByMessageId,
        } = get();
        if (isConnected) return;

        const message = dialogue.find((msg) => msg.id === id);
        if (!message) return;
        const firstSiblingIndex = dialogue.findIndex(
          (msg) => msg.parent_id === message.parent_id,
        );
        const removedMessages = dialogue.slice(firstSiblingIndex);
        const newDialogue = dialogue.slice(0, firstSiblingIndex);

        removedMessages.forEach((msg) => {
          if (msg.id) {
            clearArtifactsByMessageId(msg.id);
          }
        });

        const preferredDialogue = filterPreferredPath(newDialogue);
        fetchFromSSE({ dialogue: preferredDialogue });

        set({ dialogue: newDialogue });
      },
      emptySubmission: () => {
        const { fetchFromSSE } = get();
        // Don't clear the dialogue here. emptySubmission fires from
        // useStartChatOnMount whenever ``initialUserMessage`` is the
        // empty-string "talk-first" sentinel — which the FTUX splash
        // page sets on every nav-to-chat, including reloads of an
        // in-progress onboarding dialogue. Clearing dialogue here
        // raced with useLoadDialogue's setDialogue: if the HTTP load
        // resolved first, this would wipe the messages the user just
        // refetched; if the load was slower than the 100ms delay in
        // useStartChatOnMount, the load arrived later and the user
        // saw their chat. Same FTUX session, same code, different
        // network → "sometimes the chat is empty after I navigate
        // away and come back." For a genuinely fresh dialogue the
        // streamStore is created with dialogue=[] anyway, so this
        // clear was always a no-op in that path.
        return new Promise((resolve) => {
          fetchFromSSE({ dialogue: [] });
          resolve(true);
        });
      },
      markTurnActive: () => {
        set({
          isLoading: true,
          isTurnActive: true,
          isEnded: false,
          isMessageStreamStopped: false,
        });
      },
      continueConversation: () => {
        const { fetchFromSSE, dialogue } = get();
        return new Promise((resolve) => {
          const preferredDialogue = filterPreferredPath(dialogue);

          // Validate dialogue has messages before sending to prevent 500 errors.
          // Returns false so callers can fall back (e.g., sendUserMessage).
          if (!preferredDialogue || preferredDialogue.length === 0) {
            console.warn(
              "continueConversation: dialogue is empty, returning false for caller fallback",
            );
            resolve(false);
            return;
          }

          fetchFromSSE({ dialogue: preferredDialogue });
          resolve(true);
        });
      },
      sendUserMessage: (
        text: string,
        images?: ImageContent[],
        opts?: SendMessageOptions,
      ): Promise<boolean> => {
        return new Promise((resolve) => {
          try {
            const { fetchFromSSE, pendingImages, clearPendingImages } = get();
            // Use images from parameter if provided, otherwise use pendingImages
            const messageImages = images || pendingImages;

            set((state) => {
              try {
                const groupedMessages = groupMessages(state.dialogue);

                groupedMessages.forEach((group) => {
                  const groupContainsDPO = group.some(
                    (msg) => msg.ranking_type === DPO_FEEDBACK_TYPE,
                  );

                  if (groupContainsDPO) {
                    const allFeedbackValuesNull = group.every(
                      (msg) => msg.feedback_value === null,
                    );

                    if (allFeedbackValuesNull) {
                      throw new Error(
                        "Please select a preferred response before sending another message",
                      );
                    }
                  }
                });

                const userMessage: ChatMessageWithParts = {
                  type: "User",
                  contentParts: [{ type: ContentType.TEXT, text: text }],
                  feedback_value: null,
                  parent_id:
                    state.dialogue[state.dialogue.length - 1]?.id || null,
                  // Store images with the message for display in chat history
                  images: messageImages.length > 0 ? messageImages : undefined,
                };

                const newDialogue = [...state.dialogue, userMessage];
                const preferredDialogue = filterPreferredPath(newDialogue);

                // If there's already an active SSE connection for this dialogue,
                // use POST /chat/send to inject the message into the existing
                // session. This avoids tearing down and reconnecting the SSE
                // (which causes a race where the agent publishes events before
                // the new subscriber is ready, losing them in Redis pub/sub).
                const { isConnected, streamConfig } = get();
                const existingDialogueId = streamConfig?.dialogueId;
                if (isConnected && existingDialogueId) {
                  set({ isLoading: true, isEnded: false, isTurnActive: true });
                  const datasetIds = (
                    streamConfig?.datasetsForSearchContext || []
                  )
                    .map((d: { id?: unknown }) => d.id)
                    .filter((id): id is number => typeof id === "number");
                  // Snapshot the chart-panel view at the moment of send, so
                  // the model sees the user's current state (time window,
                  // bucket, hidden sensors). Mirrors how dataset_ids carry
                  // user-curated attachments per-message.
                  const viewContext = streamConfig?.getViewContext
                    ? streamConfig.getViewContext()
                    : undefined;
                  axios
                    .post(
                      `${nuclearn_api_url}/chat/send/${existingDialogueId}`,
                      {
                        content: text,
                        type: "message",
                        dataset_ids: datasetIds,
                        ...(viewContext ? { view_context: viewContext } : {}),
                        ...(messageImages.length > 0 && {
                          images: messageImages,
                        }),
                      },
                    )
                    .catch((err) => {
                      console.error(
                        "[sendUserMessage] chat/send failed, falling back to fetchFromSSE:",
                        err,
                      );
                      // Fallback: full reconnect if chat/send fails (e.g. no active session)
                      fetchFromSSE({
                        dialogue: preferredDialogue,
                        images:
                          messageImages.length > 0 ? messageImages : undefined,
                      });
                    });
                } else {
                  // No active SSE — use the full /chat/start → SSE flow
                  fetchFromSSE({
                    dialogue: preferredDialogue,
                    images:
                      messageImages.length > 0 ? messageImages : undefined,
                  });
                }

                // Clear pending images after sending
                clearPendingImages();

                resolve(true); // Message successfully sent
                return {
                  dialogue: newDialogue,
                  pendingImages: [],
                  // Start the latency clock at actual dispatch (both send
                  // paths above), not queue-entry — total_ms measures system
                  // latency, not how long a message waited in the queue.
                  responseTiming: {
                    sentAt: performance.now(),
                    firstTokenAt: null,
                    sendId: opts?.sendId ?? newSendId(),
                    target: opts?.target ?? "chat",
                    paired: opts?.sendId != null,
                  },
                };
              } catch (error) {
                // Catch the error and handle it here
                NucToast({
                  message: `${(error as Error).message}`,
                  type: "error",
                  hideProgressBar: false,
                  //@ts-ignore
                  toastId: "aa-send-message-error",
                });
                resolve(false); // Error occurred, message not sent
                return state;
              }
            });
          } catch (error) {
            NucToast({
              message: `${(error as Error).message}`,
              type: "error",
              hideProgressBar: false,
              //@ts-ignore
              toastId: "aa-send-message-error",
            });
            resolve(false); // Error occurred in outer try-catch
          }
        });
      },
      resendUserMessage: (
        id: string,
        text: string,
        images?: ImageContent[],
      ) => {
        const { fetchFromSSE, clearArtifactsByMessageId } = get();

        set((state) => {
          let message: ChatMessageWithParts | undefined;
          let messageIndex: number;

          if (id) {
            message = state.dialogue.find((msg) => msg.id === id);
            messageIndex = state.dialogue.findIndex((msg) => msg.id === id);
          } else {
            // Fallback: error occurred before backend assigned an ID to the
            // user message — find the last User message by type instead.
            messageIndex = state.dialogue.findLastIndex(
              (msg) => msg.type === "User",
            );
            message =
              messageIndex >= 0 ? state.dialogue[messageIndex] : undefined;
          }

          if (!message) {
            return state;
          }

          const newUserMessage: ChatMessageWithParts = {
            ...message,
            contentParts: [{ type: ContentType.TEXT, text: text }],
            // Preserve images from the edit (or undefined if all removed)
            images: images && images.length > 0 ? images : undefined,
          };
          const removedMessages = state.dialogue.slice(messageIndex + 1);

          const dialogueSlice = state.dialogue.slice(0, messageIndex);
          const updatedDialogue = [...dialogueSlice, newUserMessage];

          removedMessages.forEach((msg) => {
            if (msg.id) {
              clearArtifactsByMessageId(msg.id);
            }
          });

          const preferredDialogue = filterPreferredPath(updatedDialogue);

          fetchFromSSE({
            dialogue: preferredDialogue,
          });

          return { dialogue: updatedDialogue };
        });
      },
      setAsPreferredMessage: (messageId: string) => {
        set((state) => {
          const message = state.dialogue.find((msg) => msg.id === messageId);

          const updatedDialogue = state.dialogue.map((msg) => {
            if (msg.parent_id === message?.parent_id) {
              return {
                ...msg,
                feedback_value: msg.id === messageId ? 1 : 0,
              };
            }
            return msg;
          });

          return { dialogue: updatedDialogue };
        });
      },
      resetCoreStream: () => {
        const { abortController } = get();
        // Always abort — resetCoreStream explicitly intends to terminate all state
        if (abortController) {
          abortController.abort();
        }
        set({
          dialogue: [],
          responseSources: [],
          searchedEntries: [],
          isLoading: false,
          isConnected: false,
          isEnded: true,
          isMessageStreamStopped: true,
          connectionError: null,
          isConnectionError: false,
          streamError: "",
          errorMetadata: null,
          messages: [],
          metadata: null,
          abortController: null,
          pendingMessageQueue: [],
          responseTiming: null,
          // modelContentCount stays monotonic — watcher baselines outlive resets.
          lastUserStopAt: null,
          lastOutcomeAck: null,
          // Full reset (e.g. New chat) is the one place cards are cleared;
          // per-turn no longer wipes them so they persist across the chat.
          activeSubAgents: [],
          // Per-conversation context usage — a fresh stream starts at zero, so
          // clear it here (otherwise the context counter keeps showing the
          // previous conversation's usage after a "New chat" / reset).
          tokenUsage: null,
        });
      },

      connectToStream: (
        dialogueId: number,
        opts?: { replay?: boolean; cursor?: string },
      ) => {
        const user = authorizedUser();
        if (user.invalid) {
          redirectToLogin();
          return;
        }

        // Don't reconnect if this dialogue is already connected. A different
        // dialogue falls through; connectSSE aborts the old stream itself.
        const {
          abortController: existingCtrl,
          isConnected: alreadyConnected,
          lastDialogueId,
        } = get();
        if (alreadyConnected && existingCtrl && lastDialogueId === dialogueId)
          return;

        if (opts?.cursor) {
          // Cold load: the snapshot already holds all persisted history, so
          // keep it and resume the stream from its cursor — no wipe, no replay.
          set({
            connectionError: null,
            isConnectionError: false,
            streamError: "",
            isLoading: true,
            isEnded: false,
            isMessageStreamStopped: false,
            isTurnActive: true,
          });
          connectSSE(dialogueId, {
            skipReplay: true,
            lastEventId: opts.cursor,
          });
          return;
        }

        // Joining an in-flight turn: rebuild from DB replay, live-only would miss the prefix.
        if (opts?.replay) {
          get().resetParsingState();
          get().resetArtifactsStore();
          set({ messages: [], dialogue: [] });
        }

        set({
          connectionError: null,
          isConnectionError: false,
          streamError: "",
          isLoading: true,
          isEnded: false,
          isMessageStreamStopped: false,
          isTurnActive: true,
        });

        connectSSE(dialogueId, { skipReplay: !opts?.replay });
      },

      retryConnection: () => {
        const { lastDialogueId, resetParsingState } = get();
        if (!lastDialogueId) return;

        const user = authorizedUser();
        if (user.invalid) {
          redirectToLogin();
          return;
        }

        // Reset messages, dialogue, parsing, and artifacts so DB replay can
        // cleanly rebuild the conversation without duplicates or stale tool cards.
        resetParsingState();
        get().resetArtifactsStore();
        set({
          messages: [],
          dialogue: [],
          connectionError: null,
          isConnectionError: false,
          streamError: "",
          isLoading: true,
          isEnded: false,
          isMessageStreamStopped: false,
        });

        // Don't skip replay — backend replays from DB so we recover
        // events persisted during the disconnect. Dialogue was cleared
        // above, so replay messages rebuild it via the normal pipeline.
        connectSSE(lastDialogueId, { skipReplay: false });
      },

      queueMessage: (
        text: string,
        images?: ImageContent[],
        opts?: Pick<SendMessageOptions, "sendId">,
      ) => {
        const MAX_QUEUED_MESSAGES = 3;
        const { pendingMessageQueue } = get();
        if (pendingMessageQueue.length >= MAX_QUEUED_MESSAGES) {
          NucToast({
            message: `Maximum ${MAX_QUEUED_MESSAGES} messages can be queued. Wait for a response.`,
            type: "warning",
          });
          return;
        }
        set((state) => ({
          pendingMessageQueue: [
            ...state.pendingMessageQueue,
            { text, images, timestamp: Date.now(), sendId: opts?.sendId },
          ],
        }));
      },

      removeQueuedMessage: (index: number) => {
        set((state) => ({
          pendingMessageQueue: state.pendingMessageQueue.filter(
            (_, i) => i !== index,
          ),
        }));
      },

      processMessageQueue: async () => {
        const { isTurnActive, pendingMessageQueue, sendUserMessage } = get();
        if (isTurnActive || pendingMessageQueue.length === 0) return;

        const [nextMessage, ...rest] = pendingMessageQueue;
        const sent = await sendUserMessage(
          nextMessage.text,
          nextMessage.images,
          {
            sendId: nextMessage.sendId,
            target: "queue",
          },
        );
        if (sent) {
          set({ pendingMessageQueue: rest });
        }
      },

      clearMessageQueue: () => {
        set({ pendingMessageQueue: [] });
      },

      hydrateSubAgents: (runs: SubAgentRun[]) => {
        set((state) => {
          const seen = new Set(state.activeSubAgents.map((s) => s.dialogueId));
          const restored = runs
            .filter((r) => !seen.has(r.subagent_dialogue_id))
            .map((r) => ({
              dialogueId: r.subagent_dialogue_id,
              personaName: r.persona_name,
              status: r.status,
              parentToolCallId: r.parent_tool_call_id ?? undefined,
              messages: r.events ?? [],
            }));
          if (restored.length === 0) return {};
          return { activeSubAgents: [...state.activeSubAgents, ...restored] };
        });
      },
    };
  };
